/* A job's documents from its client, on the job's own sheet (owner, 2026-10-11: the job for the year "would also
 * be able to show intuitively whether docs are collected"; a paper copy is acknowledged "in create or edit job
 * sheet", a file attached). The list is the organisation's for the kind of work, set in the Store; one told apart
 * by a number asks for it before it is marked in, so the next work asks for each by name, and the one not provided
 * is plain. Shows nothing where no pack asks clients for documents, or the job has no client.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useToast } from '@jackdesk/shell'
import type { ErpError } from '../api'
import { useErp } from '../context'
import type { AskedDocument, DocumentChange, TaskDocuments } from './types'

const WORDS: Record<AskedDocument['status'], [string, string]> = {
  missing: ['Missing', 'chip-warn'], arrived: ['In', 'chip-ok'], not_needed: ['Not needed', ''],
}

/** The number a document still needs before it is marked in ("" when none). Pure. */
export function numberNeeded(d: Pick<AskedDocument, 'identified_by' | 'identifier'>, typed?: string): string {
  return d.identified_by && !d.identifier && !(typed ?? '').trim() ? d.identified_by : ''
}

export function WorkDocuments({ name }: { name: string }) {
  const erp = useErp()
  const toast = useToast()
  const [data, setData] = useState<TaskDocuments | null>(null)
  const [busy, setBusy] = useState(false)
  const [typed, setTyped] = useState<Record<number, string>>({})
  const [open, setOpen] = useState<{ id: number; what: 'hand' | 'why' | 'another'; text: string; forget?: boolean } | null>(null)
  const file = useRef<HTMLInputElement>(null)
  const fileFor = useRef<{ task: string; id: number; identifier?: string } | null>(null)

  const load = useCallback((signal?: AbortSignal) => {
    erp.taskDocuments(name, signal).then(setData).catch(() => setData(null))
  }, [erp, name])
  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  if (!data?.work.length) return null
  const all = data.work.flatMap((w) => w.items)
  const missing = all.filter((d) => d.status === 'missing').length

  async function change(task: string, c: DocumentChange, said: string) {
    setBusy(true)
    try {
      await erp.changeTaskDocument(name, task, c)
      setOpen(null)
      toast('ok', said)
      load()
    } catch (e) {
      toast('error', (e as ErpError).message)
    } finally {
      setBusy(false)
    }
  }

  async function attach(f: File) {
    const at = fileFor.current
    if (!at) return
    setBusy(true)
    try {
      await erp.uploadTaskDocument(name, at.task, at.id, f, at.identifier)
      toast('ok', `${f.name} kept on ${data?.client_name ?? 'the client'}.`)
      load()
    } catch (e) {
      toast('error', (e as ErpError).message)
    } finally {
      setBusy(false)
      fileFor.current = null
    }
  }

  return (
    <section className="card card-pad" aria-label="Documents from the client" style={{ display: 'grid', gap: 10 }}>
      <div className="row row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <strong>Documents from {data.client_name ?? 'the client'}</strong>
        <span className={`chip ${missing ? 'chip-warn' : 'chip-ok'}`}>
          {missing ? `${missing} of ${all.length} missing` : 'All in'}
        </span>
      </div>
      <input ref={file} type="file" hidden aria-label="The document's file"
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void attach(f) }} />
      {data.work.map((w) => {
        const lastOf = new Map(w.items.filter((d) => d.identified_by).map((d) => [d.label, d.id]))
        return (
          <div key={w.name} style={{ display: 'grid', gap: 6 }} role="group" aria-label={w.subject}>
            {data.work.length > 1 ? <div className="small"><b>{w.subject}</b></div> : null}
            {w.items.map((d) => {
              const number = typed[d.id] ?? ''
              const needs = numberNeeded(d, number)
              const identifier = d.identifier ? undefined : number.trim() || undefined
              const why = needs ? `Type the ${needs.toLowerCase()} first` : undefined
              const [word, tone] = WORDS[d.status]
              return (
                <div key={d.id} style={{ display: 'grid', gap: 4 }}>
                  <div className="row small" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className={`chip ${tone}`}>{word}</span>
                    <span style={{ flex: 1, minWidth: 180 }}>
                      {d.shown}
                      <span className="muted">
                        {d.status === 'missing' && d.copy ? ` · ${d.copy === 'hard' ? 'paper copy' : 'as a file'}` : ''}
                        {d.status === 'arrived' && d.received_as === 'hard' ? ` · paper copy in hand${d.note ? ` (${d.note})` : ''}` : ''}
                        {d.status === 'arrived' && d.file_name ? ` · ${d.file_name}` : ''}
                        {d.status === 'not_needed' && d.note ? ` · ${d.note}` : ''}
                        {d.status === 'missing' && d.asked ? ` · asked ${d.asked}×` : ''}
                      </span>
                    </span>
                    {d.status === 'missing' ? (
                      <>
                        {d.identified_by && !d.identifier ? (
                          <input className="input" style={{ width: 160, minHeight: 28 }} maxLength={80}
                                 placeholder={d.identified_by} aria-label={`${d.label}: ${d.identified_by}`} value={number}
                                 onChange={(e) => setTyped({ ...typed, [d.id]: e.target.value })} />
                        ) : null}
                        {d.copy !== 'soft' ? (
                          <button type="button" className="btn btn-quiet btn-sm" disabled={busy || Boolean(needs)} title={why}
                                  onClick={() => setOpen({ id: d.id, what: 'hand', text: '' })}>Paper copy in hand</button>
                        ) : null}
                        {d.copy !== 'hard' ? (
                          <button type="button" className="btn btn-quiet btn-sm" disabled={busy || Boolean(needs)} title={why}
                                  onClick={() => { fileFor.current = { task: w.name, id: d.id, identifier }; file.current?.click() }}>
                            Attach file
                          </button>
                        ) : null}
                        <button type="button" className="btn btn-quiet btn-sm" disabled={busy}
                                onClick={() => setOpen({ id: d.id, what: 'why', text: '' })}>Not needed</button>
                      </>
                    ) : (
                      <button type="button" className="btn btn-quiet btn-sm" disabled={busy} aria-label={`${d.label}: missing after all`}
                              onClick={() => void change(w.name, { action: 'missing', id: d.id }, 'Marked missing.')}>Undo</button>
                    )}
                  </div>
                  {open?.id === d.id && open.what === 'hand' ? (
                    <form className="row" style={{ gap: 6 }} onSubmit={(e) => {
                      e.preventDefault()
                      void change(w.name, { action: 'arrived', id: d.id, received_as: 'hard', note: open.text.trim() || undefined, identifier },
                                  `${d.label}: paper copy in hand.`)
                    }}>
                      <input className="input" style={{ flex: 1 }} autoFocus maxLength={500} placeholder="Where it is kept (optional)"
                             aria-label={`Where ${d.label} is kept`} value={open.text} onChange={(e) => setOpen({ ...open, text: e.target.value })} />
                      <button className="btn btn-sm" disabled={busy}>Acknowledge</button>
                      <button type="button" className="btn btn-quiet btn-sm" onClick={() => setOpen(null)}>Cancel</button>
                    </form>
                  ) : null}
                  {open?.id === d.id && open.what === 'why' ? (
                    <form style={{ display: 'grid', gap: 4 }} onSubmit={(e) => {
                      e.preventDefault()
                      void change(w.name, { action: 'not_needed', id: d.id, note: open.text.trim(), forget: open.forget || undefined },
                                  `${d.label}: not needed.`)
                    }}>
                      <div className="row" style={{ gap: 6 }}>
                        <input className="input" style={{ flex: 1 }} autoFocus maxLength={500} placeholder="Why, for whoever reviews it"
                               aria-label={`Why ${d.label} is not needed`} value={open.text} onChange={(e) => setOpen({ ...open, text: e.target.value })} />
                        <button className="btn btn-sm" disabled={busy || !open.text.trim()}>Save</button>
                        <button type="button" className="btn btn-quiet btn-sm" onClick={() => setOpen(null)}>Cancel</button>
                      </div>
                      {d.identifier ? (
                        <label className="check small">
                          <input type="checkbox" checked={Boolean(open.forget)} onChange={(e) => setOpen({ ...open, forget: e.target.checked })} />
                          Closed: stop asking for this one on future work
                        </label>
                      ) : null}
                    </form>
                  ) : null}
                  {lastOf.get(d.label) === d.id ? (
                    open?.id === d.id && open.what === 'another' ? (
                      <form className="row" style={{ gap: 6 }} onSubmit={(e) => {
                        e.preventDefault()
                        void change(w.name, { action: 'add', label: d.label, identifier: open.text.trim() }, `Another ${d.label.toLowerCase()} added.`)
                      }}>
                        <input className="input" style={{ flex: 1 }} autoFocus maxLength={80} placeholder={d.identified_by ?? ''}
                               aria-label={`Another ${d.label}: ${d.identified_by}`} value={open.text}
                               onChange={(e) => setOpen({ ...open, text: e.target.value })} />
                        <button className="btn btn-sm" disabled={busy || !open.text.trim()}>Add</button>
                        <button type="button" className="btn btn-quiet btn-sm" onClick={() => setOpen(null)}>Cancel</button>
                      </form>
                    ) : (
                      <div>
                        <button type="button" className="btn btn-quiet btn-sm" disabled={busy}
                                onClick={() => setOpen({ id: d.id, what: 'another', text: '' })}>
                          + Another {d.label.toLowerCase()} ({(d.identified_by ?? '').toLowerCase()})
                        </button>
                      </div>
                    )
                  ) : null}
                </div>
              )
            })}
          </div>
        )
      })}
    </section>
  )
}
